import React, { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import {
  Box, Typography, Paper, Chip, Select, MenuItem,
  FormControl, InputLabel, IconButton, Stack, Divider, Alert,
  LinearProgress, Tooltip, Button,
} from '@mui/material';
import UploadFileIcon   from '@mui/icons-material/UploadFile';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RestartAltIcon   from '@mui/icons-material/RestartAlt';
import ContentCopyIcon  from '@mui/icons-material/ContentCopy';
import CheckIcon        from '@mui/icons-material/Check';
import FolderIcon       from '@mui/icons-material/Folder';
import PushPinIcon      from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// ─── Parsing ──────────────────────────────────────────────────────────────────

function splitCSVLine(line) {
  const parts = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; }
    else if (ch === ';' && !inQ) { parts.push(cur); cur = ''; }
    else { cur += ch; }
  }
  parts.push(cur);
  return parts;
}

function rowsToEntries(rows) {
  const entries = [];
  for (let i = 1; i < rows.length; i++) {
    const p = rows[i];
    if (!p || p.length < 9) continue;

    const enhet = String(p[8] ?? '').trim();
    if (enhet !== 'tim') continue;

    const antalRaw = p[7];
    const antal = typeof antalRaw === 'number'
      ? antalRaw
      : parseFloat(String(antalRaw ?? '').trim().replace(',', '.'));
    if (isNaN(antal) || antal <= 0) continue;

    let datum = p[2];
    if (datum instanceof Date) {
      datum = isoDate(datum);
    } else if (typeof datum === 'number') {
      const info = XLSX.SSF.parse_date_code(datum);
      datum = `${info.y}-${String(info.m).padStart(2, '0')}-${String(info.d).padStart(2, '0')}`;
    } else {
      datum = String(datum ?? '').trim();
    }
    if (!datum.match(/^\d{4}-\d{2}-\d{2}$/)) continue;

    entries.push({
      kund:          String(p[0]  ?? '').trim(),
      projekt:       String(p[1]  ?? '').trim(),
      datum,
      namn:          String(p[3]  ?? '').trim(),
      aktivitet:     String(p[4]  ?? '').trim(),
      notering:      String(p[5]  ?? '').trim(),
      antal,
      projektnummer: String(p[11] ?? '').trim(),
    });
  }
  return entries;
}

function parseCSV(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  return rowsToEntries(lines.filter(l => l.trim()).map(l => splitCSVLine(l)));
}

function parseExcel(buffer) {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return rowsToEntries(XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }));
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d;
}

function weekNumber(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan4 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - jan4) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
}

const SV_DAYS   = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const SV_MONTHS = ['Januari','Februari','Mars','April','Maj','Juni',
                   'Juli','Augusti','September','Oktober','November','December'];

// Swedish decimal format for copy (e.g. 4,5)
function fmtHours(h)     { return h % 1 === 0 ? `${h}` : h.toFixed(1); }
function fmtHoursCopy(h) { return fmtHours(h).replace('.', ','); }

// ─── Month calendar overview ─────────────────────────────────────────────────

function MonthCalendar({ month, person, byDate, copied, onDayClick, accentColor, successBg }) {
  const theme = useTheme();
  if (!month) return null;

  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow    = new Date(y, m - 1, 1).getDay(); // 0=Sun
  const offset      = firstDow === 0 ? 6 : firstDow - 1; // Mon-based offset

  const cells = [
    ...Array(offset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
        {['M','T','O','T','F','L','S'].map((d, i) => (
          <Typography key={i} variant="caption"
            sx={{ textAlign: 'center', color: 'text.disabled', fontSize: '0.62rem', fontWeight: 600 }}>
            {d}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
        {cells.map((day, i) => {
          if (!day) return <Box key={i} sx={{ aspectRatio: '1' }} />;
          const dateStr   = `${month}-${String(day).padStart(2, '0')}`;
          const dayH      = (byDate[dateStr] || []).reduce((s, r) => s + r.antal, 0);
          const isCopied  = !!copied[`${person}:${dateStr}`];
          const hasH      = dayH > 0;
          const isWeekend = i % 7 >= 5;

          return (
            <Tooltip key={i} title={hasH ? `${dateStr}: ${fmtHours(dayH)}h${isCopied ? ' ✓' : ''}` : ''} arrow>
              <Box
                onClick={() => hasH && onDayClick(new Date(y, m - 1, day))}
                sx={{
                  aspectRatio: '1',
                  borderRadius: '4px',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  cursor: hasH ? 'pointer' : 'default',
                  bgcolor: isCopied
                    ? alpha(theme.palette.success.main, 0.22)
                    : hasH
                      ? alpha(accentColor, 0.15)
                      : isWeekend
                        ? alpha(theme.palette.text.primary, 0.03)
                        : 'transparent',
                  border: hasH ? '1.5px solid' : '1px solid',
                  borderColor: isCopied
                    ? theme.palette.success.main
                    : hasH
                      ? accentColor
                      : 'transparent',
                  transition: 'background-color 0.1s',
                  '&:hover': hasH ? { bgcolor: isCopied
                    ? alpha(theme.palette.success.main, 0.35)
                    : alpha(accentColor, 0.3) } : {},
                }}
              >
                <Typography sx={{
                  fontSize: '0.65rem', fontWeight: hasH ? 700 : 400, lineHeight: 1,
                  color: isCopied
                    ? theme.palette.success.main
                    : hasH ? accentColor : 'text.disabled',
                }}>
                  {day}
                </Typography>
                {hasH && (
                  <Typography sx={{ fontSize: '0.55rem', lineHeight: 1, mt: '1px',
                    color: isCopied ? 'success.main' : accentColor }}>
                    {fmtHours(dayH)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ExternTid() {
  const theme    = useTheme();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  useEffect(() => {
    getDocs(query(collection(db, 'projects'), where('status', '==', 'aktiv'), orderBy('name')))
      .then(snap => setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, []);

  const [pins, setPins] = useState(() => {
    try { return JSON.parse(localStorage.getItem('externtid_pins') || '[]'); }
    catch { return []; }
  });
  const togglePin = (id) => {
    const next = pins.includes(id) ? pins.filter(x => x !== id) : [...pins, id];
    setPins(next);
    localStorage.setItem('externtid_pins', JSON.stringify(next));
  };

  const [entries,    setEntries]    = useState([]);
  const [person,     setPerson]     = useState('');
  const [month,      setMonth]      = useState('');
  const [weekStart,  setWeekStart]  = useState(null);
  const [dragging,   setDragging]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [fileName,   setFileName]   = useState('');
  const [lastCopied, setLastCopied] = useState(null); // key of last copied field

  const [copied, setCopied] = useState(() => {
    try { return JSON.parse(localStorage.getItem('externtid_v1') || '{}'); }
    catch { return {}; }
  });

  const saveCopied = useCallback((next) => {
    setCopied(next);
    localStorage.setItem('externtid_v1', JSON.stringify(next));
  }, []);

  // ── Clipboard ─────────────────────────────────────────────────────────────

  const copyText = useCallback((text, key) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setLastCopied(key);
      setTimeout(() => setLastCopied(c => c === key ? null : c), 1800);
    });
  }, []);

  // ── File handling ──────────────────────────────────────────────────────────

  const applyParsed = useCallback((parsed) => {
    setEntries(parsed);
    const persons = [...new Set(parsed.map(r => r.namn))].sort();
    const months  = [...new Set(parsed.map(r => r.datum.slice(0, 7)))].sort();
    setPerson(persons[0] || '');
    const lastM = months[months.length - 1] || '';
    setMonth(lastM);
    if (lastM) setWeekStart(mondayOf(new Date(lastM + '-01')));
  }, []);

  const handleFile = useCallback((file) => {
    if (!file) return;
    setLoading(true);
    setFileName(file.name);

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try { applyParsed(parseExcel(new Uint8Array(e.target.result))); }
        catch (err) { console.error('ExternTid Excel error', err); }
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Try UTF-8 first; if replacement chars appear, re-read as Windows-1252
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        if (text.includes('�')) {
          // Likely Windows-1252 / Latin-1 — re-read
          const r2 = new FileReader();
          r2.onload = (e2) => {
            try { applyParsed(parseCSV(e2.target.result)); }
            catch (err) { console.error('ExternTid CSV error', err); }
            setLoading(false);
          };
          r2.readAsText(file, 'windows-1252');
        } else {
          try { applyParsed(parseCSV(text)); }
          catch (err) { console.error('ExternTid CSV error', err); }
          setLoading(false);
        }
      };
      reader.readAsText(file, 'UTF-8');
    }
  }, [applyParsed]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const reset = () => {
    setEntries([]); setPerson(''); setMonth('');
    setWeekStart(null); setFileName('');
  };

  // ── Derived data ───────────────────────────────────────────────────────────

  const persons = [...new Set(entries.map(r => r.namn))].sort();
  const months  = [...new Set(entries.map(r => r.datum.slice(0, 7)))].sort();

  const filtered = entries.filter(r =>
    (!person || r.namn === person) &&
    (!month  || r.datum.startsWith(month))
  );

  const byDate = {};
  filtered.forEach(r => { (byDate[r.datum] = byDate[r.datum] || []).push(r); });

  const totalHoursMonth = filtered.reduce((s, r) => s + r.antal, 0);

  const weekDays = weekStart
    ? Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      })
    : [];

  const daysWithEntries = weekDays.filter(d => (byDate[isoDate(d)] || []).length > 0);
  const copiedCount  = daysWithEntries.filter(d => copied[`${person}:${isoDate(d)}`]).length;
  const allCopied    = daysWithEntries.length > 0 && copiedCount === daysWithEntries.length;
  const weekFullyMarked = weekDays.length > 0 && weekDays.every(d => copied[`${person}:${isoDate(d)}`]);

  const markWeekDone = () => {
    const updates = { ...copied };
    weekDays.forEach(d => { updates[`${person}:${isoDate(d)}`] = true; });
    saveCopied(updates);
  };
  const unmarkWeek = () => {
    const updates = { ...copied };
    weekDays.forEach(d => { delete updates[`${person}:${isoDate(d)}`]; });
    saveCopied(updates);
  };

  // ── Week navigation ────────────────────────────────────────────────────────

  const stepWeek = (delta) => {
    if (!weekStart) return;
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
    // Keep month picker in sync: use the Thursday of the week (always inside ISO week's month)
    const thu = new Date(d); thu.setDate(thu.getDate() + 3);
    const m = `${thu.getFullYear()}-${String(thu.getMonth() + 1).padStart(2, '0')}`;
    if (m !== month) setMonth(m);
  };
  const prevWeek = () => stepWeek(-1);
  const nextWeek = () => stepWeek(1);

  const handleMonthChange = (m) => {
    setMonth(m);
    if (m) setWeekStart(mondayOf(new Date(m + '-01')));
  };

  const toggleDay = (dateStr) => {
    const key = `${person}:${dateStr}`;
    saveCopied({ ...copied, [key]: !copied[key] });
  };

  // ── Labels & colours ───────────────────────────────────────────────────────

  const monthLabel = month
    ? (() => { const [y, m] = month.split('-'); return `${SV_MONTHS[+m - 1]} ${y}`; })()
    : '';
  const wkNum = weekStart ? weekNumber(weekStart) : '';

  const isDark       = theme.palette.mode === 'dark';
  const accentColor  = theme.palette.secondary.main;           // #FF6B00
  const paperBg      = theme.palette.background.paper;         // resolved value
  const successBg    = alpha(theme.palette.success.main, 0.12);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 760, mx: 'auto' }}>

      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={0.5}>
        <Box>
          <Typography variant="h5" fontWeight={700}>ExternTid</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Importera tidrapport från Milltime och redovisa vecka för vecka i externa system
          </Typography>
        </Box>
        {entries.length > 0 && (
          <Tooltip title="Byt fil">
            <IconButton size="small" onClick={reset}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Project picker — always visible */}
      {(() => {
        const pinned = projects.filter(p => pins.includes(p.id));
        const others = projects.filter(p => !pins.includes(p.id));

        const ProjectRow = ({ p, isPinned }) => (
          <Stack key={p.id} direction="row" alignItems="center" spacing={0.5}>
            <Chip
              label={p.name}
              size={isPinned ? 'medium' : 'small'}
              variant={isPinned ? 'filled' : 'outlined'}
              color={isPinned ? 'secondary' : 'default'}
              onClick={() => navigate(`/projects/${p.id}?tab=externtid`)}
              sx={{ cursor: 'pointer', fontWeight: isPinned ? 700 : 400 }}
            />
            <Tooltip title={isPinned ? 'Lossa från startsidan' : 'Fäst på startsidan'}>
              <IconButton size="small" onClick={() => togglePin(p.id)}
                sx={{ color: isPinned ? 'secondary.main' : 'text.disabled',
                      '&:hover': { color: 'secondary.main' } }}>
                {isPinned
                  ? <PushPinIcon sx={{ fontSize: 14 }} />
                  : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </Tooltip>
          </Stack>
        );

        return (
          <Paper variant="outlined" sx={{ mt: 2, mb: 2, p: 1.5, backgroundColor: paperBg }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <FolderIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                Uppdrag med ExternTid
              </Typography>
              <Button size="small" variant="text"
                sx={{ color: 'text.secondary', fontSize: '0.72rem' }}
                onClick={() => navigate('/projects')}>
                Alla uppdrag →
              </Button>
            </Stack>

            {pinned.length > 0 && (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                  Fästa
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} mb={others.length ? 1.5 : 0}>
                  {pinned.map(p => <ProjectRow key={p.id} p={p} isPinned />)}
                </Stack>
              </>
            )}

            {others.length > 0 && (
              <>
                {pinned.length > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.75 }}>
                    Övriga aktiva
                  </Typography>
                )}
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {others.map(p => <ProjectRow key={p.id} p={p} isPinned={false} />)}
                </Stack>
              </>
            )}

            {projects.length === 0 && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Inga aktiva uppdrag hittades — gå till Alla uppdrag
              </Typography>
            )}
          </Paper>
        );
      })()}

      {/* Upload zone */}
      {entries.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            mt: 3, p: 6, textAlign: 'center', cursor: 'pointer',
            borderStyle: 'dashed',
            borderColor: dragging ? 'secondary.main' : 'divider',
            backgroundColor: dragging ? alpha(accentColor, 0.08) : paperBg,
            transition: 'border-color 0.2s, background-color 0.2s',
            borderRadius: 2,
            '&:hover': {
              borderColor: 'secondary.main',
              backgroundColor: alpha(accentColor, 0.04),
            },
          }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('et-file-input').click()}
        >
          <UploadFileIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }} gutterBottom>
            Dra hit eller klicka för att importera
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            CSV eller Excel från Milltime (.csv, .xlsx, .xls)
          </Typography>
          <input
            id="et-file-input" type="file" accept=".csv,.xlsx,.xls,.txt"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {entries.length > 0 && (
        <>
          <Box mt={2} mb={2}>
            <Chip
              icon={<UploadFileIcon />}
              label={fileName}
              size="small"
              variant="outlined"
              color="secondary"
            />
          </Box>

          {/* Filters */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" mb={3}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Person</InputLabel>
              <Select value={person} label="Person" onChange={e => setPerson(e.target.value)}>
                {persons.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Månad</InputLabel>
              <Select value={month} label="Månad" onChange={e => handleMonthChange(e.target.value)}>
                {months.map(m => {
                  const [y, mo] = m.split('-');
                  return <MenuItem key={m} value={m}>{SV_MONTHS[+mo - 1]} {y}</MenuItem>;
                })}
              </Select>
            </FormControl>

            {totalHoursMonth > 0 && (
              <Chip
                label={`${fmtHours(totalHoursMonth)} tim totalt`}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Week navigator */}
          {weekStart && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <IconButton size="small" onClick={prevWeek}><ChevronLeftIcon /></IconButton>
                <Typography variant="subtitle1" fontWeight={700}
                  sx={{ minWidth: 140, textAlign: 'center' }}>
                  v.{wkNum} · {monthLabel}
                </Typography>
                <IconButton size="small" onClick={nextWeek}><ChevronRightIcon /></IconButton>

                {daysWithEntries.length > 0 && (
                  <Chip
                    size="small"
                    icon={allCopied ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
                    label={`${copiedCount} / ${daysWithEntries.length} dagar klara`}
                    color={allCopied ? 'success' : 'default'}
                    variant={allCopied ? 'filled' : 'outlined'}
                  />
                )}
                <Tooltip title={weekFullyMarked ? 'Avmarkera vecka' : 'Markera hela veckan som klar'}>
                  <Chip
                    size="small"
                    icon={weekFullyMarked
                      ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />}
                    label={weekFullyMarked ? 'Vecka klar' : 'Markera vecka'}
                    color={weekFullyMarked ? 'success' : 'default'}
                    variant={weekFullyMarked ? 'filled' : 'outlined'}
                    onClick={weekFullyMarked ? unmarkWeek : markWeekDone}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              </Stack>

              {/* Two-column layout: calendar + day cards */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">

              {/* Left: month calendar */}
              <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
                <Typography variant="caption" fontWeight={600}
                  sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  {month ? (() => { const [y,m]=month.split('-'); return `${SV_MONTHS[+m-1]} ${y}`; })() : ''}
                </Typography>
                <MonthCalendar
                  month={month} person={person} byDate={byDate} copied={copied}
                  accentColor={accentColor} successBg={successBg}
                  onDayClick={(day) => setWeekStart(mondayOf(day))}
                />
              </Box>

              {/* Right: day cards */}
              <Box flex={1} sx={{ minWidth: 0, width: '100%' }}>
              <Stack spacing={1.5}>
                {weekDays.map((day, i) => {
                  const dateStr    = isoDate(day);
                  const dayEntries = byDate[dateStr] || [];
                  const isCopied   = !!copied[`${person}:${dateStr}`];
                  const totalH     = dayEntries.reduce((s, r) => s + r.antal, 0);
                  const isEmpty    = dayEntries.length === 0;

                  // ── Empty day: compact row ──────────────────────────────
                  if (isEmpty) {
                    return (
                      <Paper
                        key={dateStr}
                        variant="outlined"
                        sx={{ px: 2, py: 1, backgroundColor: paperBg }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 72 }}>
                            <Typography variant="caption"
                              sx={{ color: 'text.disabled', fontWeight: 600 }}>
                              {SV_DAYS[i]}
                            </Typography>
                            <Typography variant="body2"
                              sx={{ color: 'text.disabled', fontWeight: 700 }}>
                              {day.getDate()}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                        </Stack>
                      </Paper>
                    );
                  }

                  // ── Day with entries ────────────────────────────────────
                  return (
                    <Paper
                      key={dateStr}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        backgroundColor: isCopied ? successBg : paperBg,
                        borderColor: isCopied ? 'success.main' : undefined,
                        transition: 'background-color 0.15s, border-color 0.15s',
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={2}>

                        {/* Date column */}
                        <Box sx={{ minWidth: 64, textAlign: 'center', pt: 0.5 }}>
                          <Typography variant="caption"
                            sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                            {SV_DAYS[i]}
                          </Typography>
                          <Typography variant="h5"
                            sx={{ fontWeight: 700, lineHeight: 1.1, color: 'text.primary' }}>
                            {day.getDate()}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${fmtHours(totalH)}h`}
                            color={isCopied ? 'success' : 'secondary'}
                            variant="filled"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.72rem' }}
                          />
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        {/* Entries */}
                        <Box flex={1} sx={{ py: 0.25, minWidth: 0 }}>
                          <Stack spacing={1}>
                            {dayEntries.map((entry, j) => {
                              const hKey = `${dateStr}-${j}-h`;
                              const nKey = `${dateStr}-${j}-n`;
                              return (
                                <Box key={j}>
                                  {j > 0 && <Divider sx={{ mb: 1 }} />}
                                  <Stack direction="row" alignItems="center"
                                    spacing={0.5} flexWrap="wrap">

                                    {/* Hours + copy */}
                                    <Tooltip title={lastCopied === hKey ? 'Kopierat!' : 'Kopiera timmar'}>
                                      <Chip
                                        size="small"
                                        label={fmtHours(entry.antal)}
                                        onClick={() => copyText(fmtHoursCopy(entry.antal), hKey)}
                                        icon={lastCopied === hKey
                                          ? <CheckIcon sx={{ fontSize: '14px !important' }} />
                                          : <ContentCopyIcon sx={{ fontSize: '14px !important' }} />}
                                        sx={{
                                          cursor: 'pointer',
                                          backgroundColor: alpha(accentColor, isDark ? 0.25 : 0.12),
                                          color: isDark ? '#fff' : accentColor,
                                          fontWeight: 700,
                                          '& .MuiChip-icon': { color: 'inherit' },
                                          flexShrink: 0,
                                        }}
                                      />
                                    </Tooltip>

                                    {/* Note text */}
                                    <Typography variant="body2"
                                      sx={{
                                        flex: 1, lineHeight: 1.4,
                                        wordBreak: 'break-word', mx: 0.5,
                                        color: entry.notering ? 'text.primary' : 'text.disabled',
                                      }}>
                                      {entry.notering || <em>Ingen kommentar</em>}
                                    </Typography>

                                    {/* Copy note */}
                                    {entry.notering && (
                                      <Tooltip title={lastCopied === nKey ? 'Kopierat!' : 'Kopiera text'}>
                                        <IconButton
                                          size="small"
                                          onClick={() => copyText(entry.notering, nKey)}
                                          sx={{
                                            color: lastCopied === nKey
                                              ? 'success.main'
                                              : 'text.disabled',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {lastCopied === nKey
                                            ? <CheckIcon sx={{ fontSize: 16 }} />
                                            : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </Box>
                              );
                            })}

                            {dayEntries.length > 1 && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {dayEntries.length} rader · {fmtHours(totalH)}h totalt
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        {/* Day done toggle */}
                        <Tooltip title={isCopied ? 'Markera som ej inlagd' : 'Markera dag som klar'}>
                          <IconButton
                            size="small"
                            onClick={() => toggleDay(dateStr)}
                            color={isCopied ? 'success' : 'default'}
                            sx={{ mt: 0.5, flexShrink: 0 }}
                          >
                            {isCopied
                              ? <CheckCircleIcon />
                              : <RadioButtonUncheckedIcon />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>

              {daysWithEntries.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Ingen tid registrerad för {person} denna vecka.
                </Alert>
              )}

              {(allCopied || weekFullyMarked) && (
                <Box mt={2.5} textAlign="center">
                  <Button
                    variant="contained" color="success" onClick={nextWeek}
                    endIcon={<ChevronRightIcon />} sx={{ borderRadius: 5, px: 3 }}
                  >
                    Nästa vecka
                  </Button>
                </Box>
              )}
              </Box>{/* end right column */}
              </Stack>{/* end two-column */}
            </>
          )}
        </>
      )}
    </Box>
  );
}
